import { useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useContact } from "@/components/useContent";
import { scrollToId } from "@/App";

const TOC = [
  ["General Terms & Entity Overview", "tos-1"],
  ["Digital Beat Licensing Policies", "tos-2"],
  ["Publishing, Royalties & Content ID", "tos-3"],
  ["E-Commerce & Physical Goods", "tos-4"],
  ["Shipping & Digital Delivery", "tos-5"],
  ["Refunds, Returns & Exchanges", "tos-6"],
  ["Intellectual Property & Trademarks", "tos-7"],
  ["Governing Law & Contact Information", "tos-8"],
];

const H = ({ children }) => (
  <h2 className="font-display text-lg sm:text-xl font-bold text-white mb-4">{children}</h2>
);
const P = ({ children }) => (
  <p className="text-sm text-ice/70 leading-relaxed">{children}</p>
);
const Li = ({ children }) => (
  <li className="flex gap-2.5 text-sm text-ice/70 leading-relaxed">
    <span className="text-venom shrink-0 mt-1">▸</span>
    <span>{children}</span>
  </li>
);

const TIER_TABLE = [
  ["Standard MP3 Lease", "$29", "320 kbps MP3", "Up to 50,000", "Up to 2,000 copies", "1 Non-Monetized Video (up to 50k views)", "No Commercial Radio"],
  ["WAV Lease", "$59", "24-Bit WAV + 320 kbps MP3", "Up to 250,000", "Up to 10,000 copies", "1 Monetized Video (up to 250k views)", "Up to 2 Local Stations"],
  ["Unlimited Lease", "$149", "24-Bit WAV + 320 kbps MP3", "UNLIMITED", "UNLIMITED", "UNLIMITED Monetized Videos", "UNLIMITED Radio Broadcasts"],
  ["Exclusive Rights", "$499", "Raw Trackout Stems + 24-Bit WAV + MP3", "UNLIMITED", "UNLIMITED", "UNLIMITED Sync, TV/Film & Video", "UNLIMITED Worldwide Broadcasts"],
];

export default function TermsPage() {
  const contact = useContact();
  useEffect(() => {
    document.title = "Terms of Service & Policies | DGE: Cold-Blooded Records LLC™";
    return () => { document.title = "DGE: Cold-Blooded Records LLC™ | Official ASCAP® Member Label — Est. 2026"; };
  }, []);

  return (
    <main className="bg-obsidian text-ice min-h-screen" data-testid="terms-page">
      <Navbar />
      <div className="pt-[70px]">
        <section className="relative py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-8">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-venom font-medium mb-4">Legal</p>
              <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white leading-tight" data-testid="terms-headline">
                Terms of Service <span className="text-stroke-ice">&amp; Policies</span>
              </h1>
              <div className="mt-5 space-y-1.5 text-sm text-ice/60">
                <p><span className="text-ice/40">Last Updated:</span> September 28, 2026</p>
                <p><span className="text-ice/40">Entity:</span> DGE: Cold-Blooded Records LLC</p>
                <p>
                  <span className="text-ice/40">Official Portals:</span>{" "}
                  <a href="https://cold-blooded-records.com" className="text-crystal hover:text-venom transition-colors">cold-blooded-records.com</a>
                  {" | "}
                  <a href="https://the33rd.store" className="text-crystal hover:text-venom transition-colors">the33rd.store</a>
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <nav className="mt-10 bg-charcoal border border-crystal/25 rounded-2xl p-6" data-testid="terms-toc">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ice/40 mb-4">Table of Contents</p>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
                  {TOC.map(([label, id], i) => (
                    <button
                      key={id}
                      onClick={() => scrollToId(id)}
                      className="text-left text-sm text-ice/70 hover:text-venom transition-colors duration-300"
                      data-testid={`terms-toc-${id}`}
                    >
                      <span className="text-venom font-mono text-xs mr-2">{i + 1}.</span>{label}
                    </button>
                  ))}
                </div>
              </nav>
            </Reveal>

            <div className="mt-12 space-y-12">
              <Reveal>
                <section id="tos-1" data-testid="tos-section-1">
                  <H>1. General Terms &amp; Entity Overview</H>
                  <P>
                    Welcome to DGE: Cold-Blooded Records LLC (&quot;Company,&quot; &quot;we,&quot; &quot;us,&quot; or
                    &quot;our&quot;), operating official digital storefronts at cold-blooded-records.com and
                    the33rd.store (dba Boa&apos;s 33rd Dimension™).
                  </P>
                  <P>
                    <span className="block mt-3">
                      By accessing our websites, purchasing physical products, or licensing digital audio assets,
                      you (&quot;User&quot; or &quot;Licensee&quot;) agree to be bound by these Terms of Service and
                      Store Policies. If you do not accept these terms in full, you must discontinue use of our
                      sites and services immediately.
                    </span>
                  </P>
                </section>
              </Reveal>

              <Reveal>
                <section id="tos-2" data-testid="tos-section-2">
                  <H>2. Digital Beat Licensing Policies (Cold-Blooded Soundscapes™)</H>
                  <P>
                    All instrumental tracks, beats, and audio assets delivered on our platforms represent
                    proprietary compositions produced and copyrighted by DGE: Cold-Blooded Records LLC under our
                    signature sound lane Cold-Blooded Soundscapes™.
                  </P>
                  <P>
                    <span className="block mt-3">
                      Purchasing a digital audio asset grants you a limited, non-transferable license based
                      strictly on the tier selected at checkout:
                    </span>
                  </P>
                  <div className="mt-5 overflow-x-auto rounded-xl border border-crystal/25" data-testid="tos-tier-table">
                    <table className="w-full min-w-[760px] text-left text-xs sm:text-sm">
                      <thead>
                        <tr className="bg-charcoal text-venom font-mono text-[10px] uppercase tracking-[0.12em]">
                          {["License Tier", "Price", "Included Audio Files", "Streams Allowed", "Physical / Digital Units", "Music Video Rights", "Radio Airplay"].map((h) => (
                            <th key={h} className="px-4 py-3 font-medium">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {TIER_TABLE.map((row) => (
                          <tr key={row[0]} className="border-t border-white/8 text-ice/70">
                            {row.map((cell, j) => (
                              <td key={j} className={`px-4 py-3.5 align-top ${j === 0 ? "text-white font-semibold whitespace-nowrap" : ""} ${j === 1 ? "text-venom font-semibold" : ""}`}>
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-4 text-sm text-ice/70 leading-relaxed border-l-2 border-venom/50 pl-4">
                    <strong className="text-white">Exclusive Rights Catalog Retirement ($499):</strong> Upon
                    purchase of an Exclusive Rights License, the instrumental track is permanently retired and
                    removed from public licensing stores. No future licenses will be granted. Existing
                    non-exclusive licenses granted prior to the exclusive purchase remain valid and non-revocable.
                  </p>
                </section>
              </Reveal>

              <Reveal>
                <section id="tos-3" data-testid="tos-section-3">
                  <H>3. Publishing, Royalties &amp; Content ID</H>
                  <ul className="space-y-3">
                    <Li>
                      <span><strong className="text-white">Producer Credit Requirement:</strong> Licensee must include mandatory producer credit on all releases, streaming metadata, liner notes, and descriptions: <span className="text-venom">&quot;Produced by DGE / Cold-Blooded Records&quot;</span> or <span className="text-venom">&quot;Prod. by DGE&quot;</span></span>
                    </Li>
                    <Li>
                      <span><strong className="text-white">Publishing &amp; Writer Splits:</strong> Music Composition (Beat): 50% owned by DGE: Cold-Blooded Records LLC. Lyrics &amp; Vocal Composition: 50% owned by Licensee / Songwriter. When registering completed tracks with a Performance Rights Organization (PRO) such as BMI, ASCAP, or PRS, DGE: Cold-Blooded Records LLC must be credited as a 50% Composer/Writer.</span>
                    </Li>
                    <Li>
                      <span><strong className="text-white">Content ID &amp; Automated Claiming Systems:</strong> Non-Exclusive Licensees ($29, $59, $149) are strictly prohibited from registering derivative works with automated audio identification systems (including YouTube Content ID, DistroKid/TuneCore Content ID, Meta Rights Manager, SoundExchange). Exclusive Licensees ($499) are granted explicit authority to register derivative works with Content ID platforms.</span>
                    </Li>
                  </ul>
                </section>
              </Reveal>

              <Reveal>
                <section id="tos-4" data-testid="tos-section-4">
                  <H>4. E-Commerce &amp; Physical Goods</H>
                  <P>
                    Our official merchandise store at the33rd.store features physical apparel, accessories, and
                    footwear sub-brands, including Cold-Blooded Culture Attire™, Slither N Sparkle™, and
                    Slitherin&apos; Soles™.
                  </P>
                  <ul className="mt-4 space-y-3">
                    <Li><span><strong className="text-white">Pricing &amp; Availability:</strong> All prices are displayed in USD. We reserve the right to modify prices or adjust availability without prior notice.</span></Li>
                    <Li><span><strong className="text-white">Order Verification:</strong> We reserve the right to refuse or cancel any order suspected of fraudulent transaction activity.</span></Li>
                  </ul>
                </section>
              </Reveal>

              <Reveal>
                <section id="tos-5" data-testid="tos-section-5">
                  <H>5. Shipping &amp; Digital Delivery</H>
                  <ul className="space-y-3">
                    <Li><span><strong className="text-white">Instant Digital Delivery:</strong> Audio files (MP3, WAV, Trackout Stems) and License Agreements are delivered immediately via automated download links following payment confirmation.</span></Li>
                    <Li><span><strong className="text-white">Physical Merchandise Processing:</strong> Physical apparel and footwear orders require 2 to 5 business days for handling and order processing prior to dispatch.</span></Li>
                    <Li><span><strong className="text-white">Tracking &amp; Delivery:</strong> Shipping tracking numbers are provided via email upon dispatch. DGE: Cold-Blooded Records LLC is not liable for carrier delivery delays, lost shipments, or incorrect shipping addresses provided at checkout.</span></Li>
                  </ul>
                </section>
              </Reveal>

              <Reveal>
                <section id="tos-6" data-testid="tos-section-6">
                  <H>6. Refunds, Returns &amp; Exchanges</H>
                  <ul className="space-y-3">
                    <Li><span><strong className="text-white">Digital Media &amp; Beat Licenses:</strong> Due to the instant delivery nature of digital audio downloads, all sales of beats, sound packs, and digital media are final and strictly non-refundable.</span></Li>
                    <Li><span><strong className="text-white">Physical Apparel &amp; Footwear Exchanges:</strong> Unworn, unwashed, and un-altered physical merchandise in original packaging may be exchanged within 14 calendar days of delivery.</span></Li>
                    <Li><span><strong className="text-white">Defective Goods:</strong> If a physical item arrives damaged or defective, contact support within 7 days of delivery with photos for a free replacement or store credit.</span></Li>
                  </ul>
                </section>
              </Reveal>

              <Reveal>
                <section id="tos-7" data-testid="tos-section-7">
                  <H>7. Intellectual Property &amp; Trademarks</H>
                  <P>
                    All domain content, audio productions, graphic logos, visual media, and sub-brands — including
                    DGE: Cold-Blooded Records LLC™, Cold-Blooded Soundscapes™, Boa&apos;s 33rd Dimension™,
                    Cold-Blooded Culture Attire™, Slither N Sparkle™, and Slitherin&apos; Soles™ — are protected
                    trademarks and intellectual property of DGE: Cold-Blooded Records LLC. Unauthorized
                    duplication, distribution, or commercial exploitation is strictly prohibited.
                  </P>
                </section>
              </Reveal>

              <Reveal>
                <section id="tos-8" data-testid="tos-section-8">
                  <H>8. Governing Law &amp; Contact Information</H>
                  <P>
                    These Terms of Service are governed by and construed in accordance with the laws of the State
                    of South Carolina, United States.
                  </P>
                  <div className="mt-5 bg-charcoal border border-crystal/25 rounded-xl p-6 space-y-2 text-sm" data-testid="tos-contact-block">
                    <p className="text-ice/70"><span className="text-ice/40">Operating Entity:</span> DGE: Cold-Blooded Records LLC</p>
                    <p className="text-ice/70">
                      <span className="text-ice/40">Official Websites:</span>{" "}
                      <a href="https://cold-blooded-records.com" className="text-crystal hover:text-venom transition-colors">cold-blooded-records.com</a>
                      {" | "}
                      <a href="https://the33rd.store" className="text-crystal hover:text-venom transition-colors">the33rd.store</a>
                    </p>
                    <p className="text-ice/70"><span className="text-ice/40">Customer Support:</span> <a href={`mailto:${contact.support}`} className="text-crystal hover:text-venom transition-colors">{contact.support}</a></p>
                    <p className="text-ice/70"><span className="text-ice/40">Quotes &amp; Bookings:</span> <a href={`mailto:${contact.email}`} className="text-crystal hover:text-venom transition-colors">{contact.email}</a></p>
                    <p className="text-ice/70"><span className="text-ice/40">Phone:</span> {contact.phones.join(" · ")}</p>
                    <p className="text-ice/70"><span className="text-ice/40">Mailing Address:</span> {contact.address}</p>
                  </div>
                </section>
              </Reveal>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
}
